var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AVAILABLE_UI_THEMES, DEFAULT_UI_THEME, getDataUiTheme } from "../ui/utility/getDataUiTheme";
var UIContext = createContext(undefined);
var COLOR_MODE_STORAGE_KEY = "ui-color-mode";
var UI_THEME_STORAGE_KEY = "ui-theme";
var PENDING_TOAST_STORAGE_KEY = "ui-pending-toast";
var isStoreFeatureEnabled = function (value) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value === 1;
    }
    if (typeof value === "string") {
        var normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "1" || normalizedValue === "true";
    }
    return false;
};
var getSessionStore = function () {
    var _a;
    if (typeof window === "undefined") {
        return null;
    }
    var sessionStore = (_a = window.session) === null || _a === void 0 ? void 0 : _a.store;
    if (!sessionStore || typeof sessionStore !== "object") {
        return null;
    }
    return sessionStore;
};
var getSystemPrefersDark = function () {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
};
var focusDesktopSearchInput = function () {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }
    if (!window.matchMedia("(min-width: 769px)").matches) {
        return;
    }
    var desktopSearchForm = document.querySelector(".app-header-search.app-header-search--desktop");
    if (!desktopSearchForm) {
        return;
    }
    var formStyle = window.getComputedStyle(desktopSearchForm);
    if (formStyle.display === "none" || formStyle.visibility === "hidden") {
        return;
    }
    var searchInput = desktopSearchForm.querySelector(".app-header-search-input");
    if (!searchInput || searchInput.disabled) {
        return;
    }
    searchInput.focus();
    searchInput.select();
};
var getInitialUiTheme = function () {
    if (typeof window === "undefined") {
        return DEFAULT_UI_THEME;
    }
    var storedValue = window.localStorage.getItem(UI_THEME_STORAGE_KEY);
    if (storedValue && AVAILABLE_UI_THEMES.includes(storedValue)) {
        return storedValue;
    }
    return getDataUiTheme();
};
var getInitialColorMode = function () {
    if (typeof window === "undefined") {
        return "system";
    }
    var storedValue = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (storedValue === "light" || storedValue === "dark" || storedValue === "system") {
        return storedValue;
    }
    return "system";
};
export var UIProvider = function (_a) {
    var children = _a.children;
    var _b = useState(false), cartIsOpen = _b[0], setCartIsOpen = _b[1];
    var _c = useState(false), signInModalIsOpen = _c[0], setSignInModalIsOpen = _c[1];
    var _d = useState("signin"), authModalView = _d[0], setAuthModalView = _d[1];
    var _e = useState(false), editAddressFlyoutIsOpen = _e[0], setEditAddressFlyoutIsOpen = _e[1];
    var _f = useState(false), deleteConfirmationModalIsOpen = _f[0], setDeleteConfirmationModalIsOpen = _f[1];
    var _g = useState(false), orderTimeFlyoutIsOpen = _g[0], setOrderTimeFlyoutIsOpen = _g[1];
    var _h = useState(false), orderAddressDetailsFlyoutIsOpen = _h[0], setOrderAddressDetailsFlyoutIsOpen = _h[1];
    var _j = useState([]), toasts = _j[0], setToasts = _j[1];
    var nextToastIdRef = useRef(1);
    var _k = useState(getInitialUiTheme), uiTheme = _k[0], setUiTheme = _k[1];
    var _l = useState(getInitialColorMode), colorMode = _l[0], setColorMode = _l[1];
    var _m = useState(getSystemPrefersDark), systemPrefersDark = _m[0], setSystemPrefersDark = _m[1];
    var _o = useState(null), suggestiveSellRequest = _o[0], setSuggestiveSellRequest = _o[1];
    var sessionStore = getSessionStore();
    var storeId = typeof (sessionStore === null || sessionStore === void 0 ? void 0 : sessionStore.id) === "number" || typeof (sessionStore === null || sessionStore === void 0 ? void 0 : sessionStore.id) === "string"
        ? sessionStore.id
        : null;
    var storeName = typeof (sessionStore === null || sessionStore === void 0 ? void 0 : sessionStore.name) === "string" ? sessionStore.name.trim() : "";
    var storeSupportsApplePay = isStoreFeatureEnabled(sessionStore === null || sessionStore === void 0 ? void 0 : sessionStore.supports_applepay);
    var storeSupportsGooglePay = isStoreFeatureEnabled(sessionStore === null || sessionStore === void 0 ? void 0 : sessionStore.supports_googlepay);
    var storeUsesRewards = isStoreFeatureEnabled(sessionStore === null || sessionStore === void 0 ? void 0 : sessionStore.rr_enabled);
    var effectiveColorMode = colorMode === "system" ? (systemPrefersDark ? "dark" : "light") : colorMode;
    useEffect(function () {
        if (typeof window === "undefined") {
            return;
        }
        var pendingToastValue = window.sessionStorage.getItem(PENDING_TOAST_STORAGE_KEY);
        if (!pendingToastValue) {
            return;
        }
        window.sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY);
        try {
            var parsedPendingToast = JSON.parse(pendingToastValue);
            var hasMessage = typeof parsedPendingToast.message === "string" && parsedPendingToast.message.trim().length > 0;
            var hasValidVariant = parsedPendingToast.variant === "info"
                || parsedPendingToast.variant === "success"
                || parsedPendingToast.variant === "warning"
                || parsedPendingToast.variant === "error";
            if (!hasMessage || !hasValidVariant) {
                return;
            }
            var toastId = nextToastIdRef.current;
            nextToastIdRef.current += 1;
            setToasts([
                {
                    id: toastId,
                    open: true,
                    message: parsedPendingToast.message,
                    variant: parsedPendingToast.variant,
                    mustAcknowledge: parsedPendingToast.mustAcknowledge === true,
                },
            ]);
        }
        catch (_error) {
            return;
        }
    }, []);
    useEffect(function () {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return;
        }
        var mediaQueryList = window.matchMedia("(prefers-color-scheme: dark)");
        var handleMediaChange = function (event) {
            setSystemPrefersDark(event.matches);
        };
        setSystemPrefersDark(mediaQueryList.matches);
        mediaQueryList.addEventListener("change", handleMediaChange);
        return function () {
            mediaQueryList.removeEventListener("change", handleMediaChange);
        };
    }, []);
    useEffect(function () {
        if (colorMode !== "dark" || uiTheme === DEFAULT_UI_THEME) {
            return;
        }
        setUiTheme(DEFAULT_UI_THEME);
    }, [colorMode, uiTheme]);
    useEffect(function () {
        if (typeof window === "undefined") {
            return;
        }
        window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorMode);
        var rootElement = document.documentElement;
        rootElement.setAttribute("data-ui-theme", uiTheme);
        var sessionWindow = window;
        if (!sessionWindow.session) {
            sessionWindow.session = {};
        }
        if (!sessionWindow.session.store) {
            sessionWindow.session.store = {};
        }
        sessionWindow.session.store.theme = uiTheme;
        window.localStorage.setItem(UI_THEME_STORAGE_KEY, uiTheme);
        if (colorMode === "system") {
            rootElement.removeAttribute("data-ui-color-mode");
            rootElement.style.removeProperty("color-scheme");
            return;
        }
        rootElement.setAttribute("data-ui-color-mode", colorMode);
        rootElement.style.setProperty("color-scheme", colorMode);
    }, [colorMode, uiTheme]);
    useEffect(function () {
        if (typeof window === "undefined") {
            return;
        }
        var handleSearchFocus = function (event) {
            var isSlashKey = event.key === "/";
            if (!isSlashKey) {
                return;
            }
            focusDesktopSearchInput();
            var activeElement = document.activeElement;
            var focusedSearchInput = activeElement instanceof HTMLInputElement && activeElement.classList.contains("app-header-search-input");
            if (focusedSearchInput) {
                event.preventDefault();
            }
        };
        window.addEventListener("keydown", handleSearchFocus);
        return function () {
            window.removeEventListener("keydown", handleSearchFocus);
        };
    }, []);
    var openCart = useCallback(function () {
        setCartIsOpen(true);
    }, []);
    var closeCart = useCallback(function () {
        setCartIsOpen(false);
    }, []);
    var openEditAddressFlyout = useCallback(function () {
        setEditAddressFlyoutIsOpen(true);
    }, []);
    var closeEditAddressFlyout = useCallback(function () {
        setEditAddressFlyoutIsOpen(false);
    }, []);
    var openDeleteConfirmationModal = useCallback(function () {
        setDeleteConfirmationModalIsOpen(true);
    }, []);
    var closeDeleteConfirmationModal = useCallback(function () {
        setDeleteConfirmationModalIsOpen(false);
    }, []);
    var openOrderTimeFlyout = useCallback(function () {
        setOrderTimeFlyoutIsOpen(true);
    }, []);
    var closeOrderTimeFlyout = useCallback(function () {
        setOrderTimeFlyoutIsOpen(false);
    }, []);
    var openOrderAddressDetailsFlyout = useCallback(function () {
        setOrderAddressDetailsFlyoutIsOpen(true);
    }, []);
    var closeOrderAddressDetailsFlyout = useCallback(function () {
        setOrderAddressDetailsFlyoutIsOpen(false);
    }, []);
    var openSignInModal = useCallback(function () {
        setAuthModalView("signin");
        setSignInModalIsOpen(true);
    }, []);
    var openSignUpModal = useCallback(function () {
        setAuthModalView("signup");
        setSignInModalIsOpen(true);
    }, []);
    var closeSignInModal = useCallback(function () {
        setSignInModalIsOpen(false);
    }, []);
    var showToast = useCallback(function (message, variant, mustAcknowledge, action) {
        if (variant === void 0) { variant = "info"; }
        if (mustAcknowledge === void 0) { mustAcknowledge = false; }
        var toastId = nextToastIdRef.current;
        nextToastIdRef.current += 1;
        setToasts(function (previous) { return __spreadArray(__spreadArray([], previous, true), [
            {
                id: toastId,
                open: true,
                message: message,
                variant: variant,
                mustAcknowledge: mustAcknowledge,
                action: action,
            },
        ], false); });
    }, []);
    var hideToast = useCallback(function () {
        setToasts(function (previous) {
            var _loop_1 = function (index) {
                var toast_1 = previous[index];
                if (!toast_1.open) {
                    return "continue";
                }
                return { value: previous.map(function (candidate) {
                        if (candidate.id !== toast_1.id) {
                            return candidate;
                        }
                        return __assign(__assign({}, candidate), { open: false });
                    }) };
            };
            for (var index = previous.length - 1; index >= 0; index -= 1) {
                var state_1 = _loop_1(index);
                if (typeof state_1 === "object")
                    return state_1.value;
            }
            return previous;
        });
    }, []);
    var setToast = useCallback(function (toast) {
        setToasts(function (previous) {
            if (!previous.length) {
                if (!toast.open) {
                    return previous;
                }
                var toastId = toast.id || nextToastIdRef.current;
                if (!toast.id) {
                    nextToastIdRef.current += 1;
                }
                return [__assign(__assign({}, toast), { id: toastId })];
            }
            var latestToast = previous[previous.length - 1];
            return __spreadArray(__spreadArray([], previous.slice(0, -1), true), [
                __assign(__assign({}, toast), { id: toast.id || latestToast.id }),
            ], false);
        });
    }, []);
    var toast = useMemo(function () {
        if (!toasts.length) {
            return {
                id: 0,
                open: false,
                message: "",
                variant: "info",
            };
        }
        return toasts[toasts.length - 1];
    }, [toasts]);
    var openSuggestiveSell = useCallback(function (itemNavigationId, source) {
        if (source === void 0) { source = "cart_add"; }
        setSuggestiveSellRequest({
            itemNavigationId: itemNavigationId,
            source: source,
            requestId: Date.now(),
        });
    }, []);
    var closeSuggestiveSell = useCallback(function () {
        setSuggestiveSellRequest(null);
    }, []);
    var toggleDarkMode = useCallback(function () {
        var currentMode = colorMode === "system"
            ? (systemPrefersDark ? "dark" : "light")
            : colorMode;
        var nextMode = currentMode === "dark" ? "light" : "dark";
        if (nextMode === "dark") {
            setUiTheme(DEFAULT_UI_THEME);
        }
        setColorMode(nextMode);
    }, [colorMode, systemPrefersDark]);
    var value = useMemo(function () { return ({
        storeId: storeId,
        storeName: storeName,
        storeSupportsApplePay: storeSupportsApplePay,
        storeSupportsGooglePay: storeSupportsGooglePay,
        storeUsesRewards: storeUsesRewards,
        cartIsOpen: cartIsOpen,
        openCart: openCart,
        closeCart: closeCart,
        setCartIsOpen: setCartIsOpen,
        signInModalIsOpen: signInModalIsOpen,
        authModalView: authModalView,
        openSignInModal: openSignInModal,
        openSignUpModal: openSignUpModal,
        closeSignInModal: closeSignInModal,
        setSignInModalIsOpen: setSignInModalIsOpen,
        setAuthModalView: setAuthModalView,
        editAddressFlyoutIsOpen: editAddressFlyoutIsOpen,
        openEditAddressFlyout: openEditAddressFlyout,
        closeEditAddressFlyout: closeEditAddressFlyout,
        setEditAddressFlyoutIsOpen: setEditAddressFlyoutIsOpen,
        deleteConfirmationModalIsOpen: deleteConfirmationModalIsOpen,
        openDeleteConfirmationModal: openDeleteConfirmationModal,
        closeDeleteConfirmationModal: closeDeleteConfirmationModal,
        setDeleteConfirmationModalIsOpen: setDeleteConfirmationModalIsOpen,
        orderTimeFlyoutIsOpen: orderTimeFlyoutIsOpen,
        openOrderTimeFlyout: openOrderTimeFlyout,
        closeOrderTimeFlyout: closeOrderTimeFlyout,
        setOrderTimeFlyoutIsOpen: setOrderTimeFlyoutIsOpen,
        orderAddressDetailsFlyoutIsOpen: orderAddressDetailsFlyoutIsOpen,
        openOrderAddressDetailsFlyout: openOrderAddressDetailsFlyout,
        closeOrderAddressDetailsFlyout: closeOrderAddressDetailsFlyout,
        setOrderAddressDetailsFlyoutIsOpen: setOrderAddressDetailsFlyoutIsOpen,
        toast: toast,
        toasts: toasts,
        showToast: showToast,
        hideToast: hideToast,
        setToast: setToast,
        setToasts: setToasts,
        uiTheme: uiTheme,
        availableUiThemes: AVAILABLE_UI_THEMES,
        setUiTheme: setUiTheme,
        colorMode: colorMode,
        effectiveColorMode: effectiveColorMode,
        setColorMode: setColorMode,
        toggleDarkMode: toggleDarkMode,
        suggestiveSellRequest: suggestiveSellRequest,
        openSuggestiveSell: openSuggestiveSell,
        closeSuggestiveSell: closeSuggestiveSell,
    }); }, [
        storeId,
        storeName,
        storeSupportsApplePay,
        storeSupportsGooglePay,
        storeUsesRewards,
        cartIsOpen,
        openCart,
        closeCart,
        signInModalIsOpen,
        authModalView,
        openSignInModal,
        openSignUpModal,
        closeSignInModal,
        editAddressFlyoutIsOpen,
        openEditAddressFlyout,
        closeEditAddressFlyout,
        deleteConfirmationModalIsOpen,
        openDeleteConfirmationModal,
        closeDeleteConfirmationModal,
        orderTimeFlyoutIsOpen,
        openOrderTimeFlyout,
        closeOrderTimeFlyout,
        orderAddressDetailsFlyoutIsOpen,
        openOrderAddressDetailsFlyout,
        closeOrderAddressDetailsFlyout,
        toast,
        toasts,
        showToast,
        hideToast,
        setToast,
        setToasts,
        uiTheme,
        setUiTheme,
        colorMode,
        effectiveColorMode,
        toggleDarkMode,
        suggestiveSellRequest,
        openSuggestiveSell,
        closeSuggestiveSell,
    ]);
    return (React.createElement(UIContext.Provider, { value: value }, children));
};
export var useUI = function () {
    var context = useContext(UIContext);
    if (!context) {
        throw new Error("useUI must be used within a UIProvider");
    }
    return context;
};
